import { defineStore } from "pinia";
import { ref, computed } from "vue";
import { useRest } from "@/composables";

export const useCalabozoStore = defineStore("calabozo", () => {
  const detenidos = ref([]);
  const isLoading = ref(false);
  const selectedDetenido = ref(null);
  const isDetailLoading = ref(false);
  const currentPage = ref(1);
  const pageSize = ref(7);
  const estadoFilter = ref("EN_CALABOZO");
  const search = ref({});
  const directFilters = ref({});
  const sortField = ref(null);
  const sortOrder = ref(-1);
  const rest = useRest();
  const totalDocs = ref(0);

  const detencionPorActuacionPersonaFisicaId = ref({});
  const loadingPorActuacionPersonaFisicaId = ref({}); // { [actuacionId]: boolean }

  const calabozoLabelFromApi = (raw) => {
    const v = (raw ?? "").toString().trim().toUpperCase();

    const map = {
      EN_CALABOZO: "En Calabozo",
      PENDIENTE: "Pendiente",
      LIBERADO: "Liberado",
    };

    return map[v] ?? "";
  };

  const filteredDetenidos = computed(() => {
    if (!estadoFilter.value) return detenidos.value;
    return detenidos.value.filter((d) => d.estado === estadoFilter.value);
  });

  const totalPages = computed(() => {
    if (totalDocs.value === 0) return 1;
    return Math.ceil(totalDocs.value / pageSize.value);
  });

  const offset = computed(() => (currentPage.value - 1) * pageSize.value);

  const paginatedDetenidos = computed(() => {
    const start = offset.value;
    const end = start + pageSize.value;
    return filteredDetenidos.value.slice(start, end);
  });

  const setEstadoFilter = (estado) => {
    estadoFilter.value = estado;
    currentPage.value = 1;
  };

  const GetDetencionById = async (detencionId) => {
    if (!detencionId) return null;
    isLoading.value = true;
    try {
      const url = `${process.env.VUE_APP_BACKEND_ENVIRONMENT}/calabozo/detenciones/${detencionId}`;
      const { data } = await rest.get({ url });
      return data?.data?.[0] ?? null;
    } catch (e) {
      console.error("[calabozo.fetch]", e);
      return null;
    } finally {
      isLoading.value = false;
    }
  };
  const getDetenciones = async (filtersParam = null, isForDetail = false) => {
    isLoading.value = true;
    try {
      const url = `${process.env.VUE_APP_BACKEND_ENVIRONMENT}/calabozo/detenciones/`;
      if (filtersParam && typeof filtersParam === "object") {
        const hasSearchKey = Object.prototype.hasOwnProperty.call(filtersParam, "search");
        const hasDirectKey = Object.prototype.hasOwnProperty.call(filtersParam, "direct");
        const shouldResetFilters = Object.keys(filtersParam).length === 0;

        if (hasSearchKey || hasDirectKey || shouldResetFilters) {
          search.value = filtersParam.search || {};
          directFilters.value = filtersParam.direct || {};
          currentPage.value = 1;
        }
      }

      const params = {
        limit: pageSize.value,
        offset: offset.value || 0,
        order: sortField.value
          ? `${sortField.value} ${sortOrder.value === 1 ? "ASC" : "DESC"}`
          : null,
        getTotal: true,
        EstadoActual: estadoFilter.value,
        ...directFilters.value,
      };

      if (Object.keys(search.value || {}).length > 0) {
        params.search = JSON.stringify(search.value);
      }

      const { data } = await rest.get({ url, params });
      totalDocs.value = data.total;
      detenidos.value = Array.isArray(data?.data) ? data.data : [];
    } catch (e) {
      console.error("[calabozo.fetch]", e);
    } finally {
      if (!isForDetail) isLoading.value = false;
    }
  };

  const fetchDetencionPorActuacionPersonaFisicaId = async (actuacionPersonaFisicaID) => {
    if (!actuacionPersonaFisicaID) return;

    if (detencionPorActuacionPersonaFisicaId.value[actuacionPersonaFisicaID] !== undefined) return;

    if (loadingPorActuacionPersonaFisicaId.value[actuacionPersonaFisicaID]) return;

    loadingPorActuacionPersonaFisicaId.value[actuacionPersonaFisicaID] = true;

    try {
      const url = `${process.env.VUE_APP_BACKEND_ENVIRONMENT}/calabozo/detenciones/`;
      const params = {
        search: {
          "exp_pre_CalabozoDetencion.ActuacionPersonaFisicaID": [
            { operator: "=", value: actuacionPersonaFisicaID },
          ],
        },
      };

      const { data } = await rest.get({ url, params });
      detencionPorActuacionPersonaFisicaId.value = {
        ...detencionPorActuacionPersonaFisicaId.value,
        [actuacionPersonaFisicaID]: data?.data?.[0] ?? null,
      };
    } catch (e) {
      detencionPorActuacionPersonaFisicaId.value = {
        ...detencionPorActuacionPersonaFisicaId.value,
        [actuacionPersonaFisicaID]: null,
      };
    } finally {
      loadingPorActuacionPersonaFisicaId.value[actuacionPersonaFisicaID] = false;
    }
  };

  let loadDetenidoDetalleToken = 0;
  const loadDetenidoDetalle = async (detencionID, isRefreshNedeed = false) => {
    if (!detencionID) return;
    loadDetenidoDetalleToken += 1;
    const requestId = loadDetenidoDetalleToken;
    isDetailLoading.value = true;
    selectedDetenido.value = null;
    try {
      let base = detenidos.value.find((d) => String(d.DetencionID) === String(detencionID));
      if (!base || isRefreshNedeed) {
        base = await GetDetencionById(detencionID);
      }
      if (requestId !== loadDetenidoDetalleToken) return;
      if (!base) {
        selectedDetenido.value = null;
        return;
      }

      const url = `${process.env.VUE_APP_BACKEND_ENVIRONMENT}/persona-fisica/${base.PersonaFisicaID}`;
      const { data } = await rest.get({ url });
      if (requestId !== loadDetenidoDetalleToken) return;

      const persona = data?.data?.[0] ?? null;

      selectedDetenido.value = {
        ...persona,
        ...base,
        tipoVinculado: "Detenido",
      };
    } catch (e) {
      console.error("[calabozo.fetch]", e);
    } finally {
      if (requestId === loadDetenidoDetalleToken) {
        isDetailLoading.value = false;
      }
    }
  };

  const registrarIngreso = async (detenidoId, payload) => {
    const index = detenidos.value.findIndex((d) => d.DetencionID === detenidoId);
    if (index === -1) return;
    try {
      const body = {
        EstadoVisual: payload.estadoSalud,
        EfectosRetenidos: payload.objetosRetenidos,
        FechaIngreso: payload.fechaHora,
        Dni: payload.dni,
      };
      let options = {
        url: `${process.env.VUE_APP_BACKEND_ENVIRONMENT}/calabozo/detenciones/${detenidoId}/ingreso`,
        formData: body,
        ContentType: "application/json",
      };
      await rest.put(options);
    } catch (error) {
      console.error("Error al registrar ingreso", error);
    } finally {
    }
  };

  const registrarEgreso = async (detenidoId, payload) => {
    const index = detenidos.value.findIndex((d) => d.DetencionID === detenidoId);
    if (index === -1) return;
    try {
      const body = {
        MotivoLiberacion: payload.motivos,
        FormaLiberacion: payload.formaLiberacion,
        FechaEgreso: payload.fechaHora,
      };
      let options = {
        url: `${process.env.VUE_APP_BACKEND_ENVIRONMENT}/calabozo/detenciones/${detenidoId}/egreso`,
        formData: body,
        ContentType: "application/json",
      };
      await rest.put(options);
    } catch (error) {
      console.error("Error al registrar egreso", error);
    } finally {
    }
  };

  const nextPage = async () => {
    if (currentPage.value < totalPages.value) {
      currentPage.value++;
    }
    await getDetenciones();
  };

  const prevPage = async () => {
    if (currentPage.value > 1) {
      currentPage.value--;
    }
    await getDetenciones();
  };

  const goToPage = async (page) => {
    if (page >= 1 && page <= totalPages.value) {
      currentPage.value = page;
    }
    await getDetenciones();
  };

  const handlePageChange = async ({ page, rows }) => {
    currentPage.value = page + 1;
    pageSize.value = rows;
    await getDetenciones();
  };

  const clearSelectedDetenido = () => {
    selectedDetenido.value = null;
  };

  const getAppliedFilters = () => {
    const appliedFilters = {};

    Object.entries(search.value || {}).forEach(([field, operations]) => {
      if (Array.isArray(operations) && operations.length > 0) {
        if (
          operations.length > 1 ||
          (operations[0].operator && [">=", "<="].includes(operations[0].operator))
        ) {
          appliedFilters[field] = {
            key: field,
            label: field,
            field: field,
            operator: null,
            value: operations,
            queryType: "search",
            displayValue: "",
            visible: true,
          };
        } else {
          const operation = operations[0];
          appliedFilters[field] = {
            key: field,
            label: field,
            field: field,
            operator: operation.operator || "=",
            value: operation.value,
            queryType: "search",
            displayValue: operation.value,
            visible: true,
          };
        }
      }
    });

    Object.entries(directFilters.value || {}).forEach(([field, value]) => {
      appliedFilters[field] = {
        key: field,
        label: field,
        field: field,
        operator: "=",
        value: value,
        queryType: "direct",
        displayValue: value,
        visible: true,
      };
    });

    return appliedFilters;
  };

  return {
    detencionPorActuacionPersonaFisicaId,
    loadingPorActuacionPersonaFisicaId,
    calabozoLabelFromApi,
    fetchDetencionPorActuacionPersonaFisicaId,
    detenidos,
    isLoading,
    estadoFilter,
    search,
    directFilters,
    selectedDetenido,
    isDetailLoading,
    currentPage,
    pageSize,
    totalDocs,
    totalPages,
    offset,
    paginatedDetenidos,
    setEstadoFilter,
    nextPage,
    prevPage,
    goToPage,
    getDetenciones,
    handlePageChange,
    loadDetenidoDetalle,
    clearSelectedDetenido,
    registrarIngreso,
    registrarEgreso,
    getAppliedFilters,
  };
});
